import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useState } from "react";
import { StarIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { normalizedTrailRating, trailCommunityRatingText } from "@/lib/trailRatings";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

function Stars({ value, onSelect, disabled = false, size = 22 }) {
  const normalized = normalizedTrailRating(value);
  return (
    <div className="flex items-center gap-0.5" aria-label={translateLabel("{value} out of 5 stars", { value: normalized })}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(star)}
          data-testid={`trail-rating-star-${star}`}
          aria-label={translateLabel(star === 1 ? "Rate {value} star" : "Rate {value} stars", { value: star })}
          className={`transition-transform hover:scale-110 disabled:cursor-wait ${star <= normalized ? "text-[#CCFF00]" : "text-zinc-600 hover:text-zinc-400"}`}
        >
          <StarIcon size={size} weight={star <= normalized ? "fill" : "regular"} />
        </button>
      ))}
    </div>
  );
}

export function TrailCommunityRating({ trailId, variant = "block" }) {
  const navigate = useNavigate();
  const { user, requestTrailRating } = useAuth();
  const [summary, setSummary] = useState({ average: null, count: 0 });
  const [ownRating, setOwnRating] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const publicRequest = api.get(`/trails/${trailId}/rating`);
      const ownRequest = user ? api.get(`/trails/${trailId}/rating/mine`) : Promise.resolve({ data: null });
      const [publicResult, ownResult] = await Promise.all([publicRequest, ownRequest]);
      setSummary(publicResult.data);
      setOwnRating(ownResult.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [trailId, user]);

  useEffect(() => { load(); }, [load]);

  const setRating = async (rating) => {
    if (!user) {
      requestTrailRating(trailId, rating);
      toast(translateLabel("Sign in to rate this trail"), { description: translateLabel("{rating}★ will be applied after sign-in.", { rating }) });
      navigate("/login");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post(`/trails/${trailId}/rating`, { rating });
      setOwnRating(data);
      const { data: nextSummary } = await api.get(`/trails/${trailId}/rating`);
      setSummary(nextSummary);
      toast.success(translateLabel("Trail rating saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const clearRating = async () => {
    if (!ownRating) return;
    setSaving(true);
    try {
      await api.delete(`/trails/${trailId}/rating`);
      setOwnRating(null);
      const { data: nextSummary } = await api.get(`/trails/${trailId}/rating`);
      setSummary(nextSummary);
      toast.success(translateLabel("Trail rating cleared"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inline = variant === "inline";

  if (loading) {
    return (
      <Skeleton
        className={inline ? "h-11 w-full min-w-0 rounded-full bg-white/5 sm:w-64" : "mb-6 h-20 w-full rounded-2xl bg-white/5"}
        data-testid="trail-rating-loading"
      />
    );
  }

  if (error) {
    return (
      <section
        className={inline
          ? "flex items-center gap-2 rounded-full border border-red-500/20 bg-red-500/5 px-3 py-2"
          : "mb-6 rounded-2xl border border-red-500/20 bg-red-500/5 p-4"}
        data-testid="trail-rating-error"
      >
        {!inline && <p className="text-sm font-semibold text-red-300">{error}</p>}
        <button
          type="button"
          onClick={load}
          className="text-xs font-bold text-[#CCFF00]"
          data-testid="trail-rating-retry"
        >
          {translateLabel("Rating unavailable · retry")}
        </button>
      </section>
    );
  }

  if (inline) {
    return (
      <section
        className="flex h-12 min-w-0 items-center gap-2 rounded-xl border border-[#CCFF00]/20 bg-[#CCFF00]/[0.06] px-3 sm:min-w-[17rem] sm:gap-3"
        data-testid="trail-community-rating"
      >
        <div className="min-w-0 shrink-0">
          <div className="hidden text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 sm:block">{translateLabel("Community")}</div>
          <div className="truncate font-display text-xs font-bold leading-tight text-white sm:text-sm" data-testid="trail-rating-summary">
            {trailCommunityRatingText(summary)}
          </div>
        </div>
        <div className="ml-auto flex min-w-0 items-center gap-1.5">
          <Stars value={ownRating?.rating ?? 0} onSelect={setRating} disabled={saving} size={16} />
          {ownRating && (
            <button
              type="button"
              disabled={saving}
              onClick={clearRating}
              data-testid="clear-trail-rating"
              className="ml-1 text-[10px] font-bold text-zinc-500 hover:text-white disabled:opacity-50"
            >
              {translateLabel("Clear")}
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="mb-6 rounded-2xl border border-[#CCFF00]/20 bg-[#CCFF00]/5 px-4 py-3" data-testid="trail-community-rating">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
        <div className="min-w-0">
          <div className="label-tiny text-[#CCFF00]">{translateLabel("BPF community trail rating")}</div>
          <div className="mt-1 font-display text-base font-bold text-white sm:text-lg" data-testid="trail-rating-summary">
            {trailCommunityRatingText(summary)}
          </div>
          {summary.count === 0 && <p className="mt-0.5 hidden text-xs text-zinc-500 sm:block">{translateLabel("Be the first to rate this trail.")}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-right">
          <div>
            <Stars value={ownRating?.rating ?? 0} onSelect={setRating} disabled={saving} />
            <div className="mt-1 text-[11px] text-zinc-500">{ownRating ? translateLabel("Your rating: {rating}★", { rating: ownRating.rating }) : translateLabel("Rate this trail")}</div>
          </div>
          {ownRating && (
            <button
              type="button"
              disabled={saving}
              onClick={clearRating}
              data-testid="clear-trail-rating"
              className="text-[11px] font-bold text-zinc-400 underline-offset-4 hover:text-white hover:underline disabled:opacity-50"
            >
              {translateLabel("Clear")}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
